import { useMemo, Fragment, createElement, useState, useCallback } from 'react';
import { useLoaderData, useSearchParams } from 'react-router-dom';
import { apiSearchSlice } from "@features/api/apiSearchSlice";
import { getInitialUrl, setRequestToSearchParams } from '@utils/index';
import type { IGetArticleParams, IGetArticle, IContentData, ILoaderData, INshop, ITotal } from '@type/index';
import { ALIAS, BE_ALIAS, CUR_NETWORK, NETWORK, NSHOP_ALIAS, NSHOP_DOMAIN, SEARCH_PARAMS, SERVICE_ALIAS, COMMUNITY_SERVER_TYPE, GAME_NAME, TOTAL_SORT } from '@constants/index';
import { getLocalData } from '@features/api/apiLocalSlice';
import { NoData } from '../../components/contents/common/NoData';
import { useOption } from '@hooks/useOption';
import { useAppSelector } from '@hooks/defineRtkTypedHook';
import { selectL10N, setAlias } from '@features/ui/UiSlice';
import { NshopArticle } from '@components/contents/article/NshopArticle';
import { PowerbookArticle } from '@components/contents/article/PowerbookArticle';
import { CommunityArticle } from '@components/contents/article/CommunityArticle';
import { TagArticle } from '@components/contents/article/TagArticle';
import { FaqArticle } from '@components/contents/article/FaqArticle';
import { GuidebookArticle } from '@components/contents/article/GuideBookArticle';
import { DictionaryArticle } from '@components/contents/article/DictionaryArticle';
import { CharacterBnsArticle } from '@components/contents/article/CharacterBnsArticle';
import { CharacterAionArticle } from '@components/contents/article/CharacterAionArticle';
import { LegionArticle } from '@components/contents/article/LegionArticle';
import { GuildArticle } from '@components/contents/article/GuildArticle';
import { NpUserArticle } from '@components/contents/article/NpUserArticle';
import { VideoArticle } from '@components/contents/article/VideoArticle';
import { NctvArticle } from '@components/contents/article/NctvArticle';
import { EventArticle } from '@components/contents/article/EventArticle';
import { ImageArticle } from '@components/contents/article/ImageArticle';
import { ContentListArticle } from '@components/contents/article/ContentListArticle';
import { TradeArticle, getTradeMoreUrl } from '@components/contents/article/TradeArticle';
import { AllMore } from '@components/contents/common/AllMore';
import { IModal, Modal } from '@components/contents/common/Modal';

export const TotalLoader = (dispatch, option: IOption) =>
async ({ request, params }): Promise<ITotal<typeof serviceAlias> | null> => {
    const searchParams = setRequestToSearchParams(request);

    // alias 변경 - searchParams 변경에 사용됨
    dispatch(setAlias(ALIAS.INDEX));

    if(!searchParams?.keyword) {
        return null;
    }
    
    const { serviceAlias } = option;
    return CUR_NETWORK === NETWORK.OPDEV ? await getLocalData(`${ALIAS.INDEX}_${serviceAlias}`, searchParams) : await dispatch(apiSearchSlice?.endpoints?.getTotal?.initiate({ option, params: searchParams }));
};

const MAX_LENGTH = 4;
const getArticle = ({option, l10n, alias, searchParams}: IGetArticleParams): IGetArticle => {
    const { serviceAlias } = option || {};
    let component;
    let column = 2;
    let maxLength = MAX_LENGTH;
    let key = alias || '';
    let title = alias ? l10n?.alias[alias] : '';
    let moreName = title ? `${title} ${l10n.template.more}` : l10n.template.more;
    let moreUrl = alias ? `${getInitialUrl({ serviceAlias, alias, url: `/${alias}`, searchParams })}` : '';
    let moreUrlType = 'ROUTE';

    switch(alias) {
        case BE_ALIAS.POWERBOOK:
            component = PowerbookArticle;
            column = 2;
            break;
        case BE_ALIAS.COMMUNITY:
        case BE_ALIAS.AWAKENING_COMMUNITY:
            {
                component = CommunityArticle;
                column = 1;
                key = ALIAS.COMMUNITY;

                if(serviceAlias === SERVICE_ALIAS.LINEAGE2) {
                    const isAwaken = alias === BE_ALIAS.AWAKENING_COMMUNITY;
                    title = `${alias === BE_ALIAS.AWAKENING_COMMUNITY ? `${l10n?.filter?.l2AwknServer} ` : `${l10n?.filter?.l2Server} `}${l10n?.alias?.community}`;
                    moreName = `${title} ${l10n.template.more}`;    
                    moreUrl = `${getInitialUrl({ serviceAlias, alias: key, url: `/${key}`, searchParams: {
                        [SEARCH_PARAMS.KEYWORD]: searchParams?.get(SEARCH_PARAMS.KEYWORD) || '',
                        [SEARCH_PARAMS.SERVER]: isAwaken ? COMMUNITY_SERVER_TYPE[SERVICE_ALIAS.LINEAGE2].L2AWKN : COMMUNITY_SERVER_TYPE[SERVICE_ALIAS.LINEAGE2].L2
                    }})}`;
                } else {
                    title = `${l10n?.alias?.community}`;
                    moreName = `${title} ${l10n.template.more}`;
                    moreUrl = `${getInitialUrl({ serviceAlias, alias: key, url: `/${key}`, searchParams })}`;
                }
            }
            break;
        case BE_ALIAS.TAG:
        case BE_ALIAS.AWAKENING_TAG:
            {
                component = TagArticle;
                column = 2;
                maxLength = 6;
                key = ALIAS.TAG;

                if(serviceAlias === SERVICE_ALIAS.LINEAGE2) {
                    const isAwaken = alias === BE_ALIAS.AWAKENING_TAG;
                    title = `${isAwaken ? `${l10n?.filter?.l2AwknServer} ` : `${l10n?.filter?.l2Server} `}${l10n?.alias?.tagList}`;
                    moreName = `${title} ${l10n.template.more}`;
                    moreUrl = `${getInitialUrl({ serviceAlias, alias: key, url: `/${key}`, searchParams: {
                        [SEARCH_PARAMS.KEYWORD]: searchParams?.get(SEARCH_PARAMS.KEYWORD) || '',
                        [SEARCH_PARAMS.SERVER]: isAwaken ? COMMUNITY_SERVER_TYPE[SERVICE_ALIAS.LINEAGE2].L2AWKN : COMMUNITY_SERVER_TYPE[SERVICE_ALIAS.LINEAGE2].L2
                    }})}`;
                } else {
                    title = `${l10n?.alias?.tagList}`;
                    moreName = `${title} ${l10n.template.more}`;
                    moreUrl = `${getInitialUrl({ serviceAlias, alias: key, url: `/${key}`, searchParams })}`;
                }
            }
            break;
        case BE_ALIAS.FAQ:
            component = FaqArticle;
            column = 2;
            break;
        case BE_ALIAS.GUIDEBOOK:
        case BE_ALIAS.CLASSIC_GUIDEBOOK:
        case BE_ALIAS.AWAKENING_GUIDEBOOK:
            {
                component = GuidebookArticle;
                column = 2;
                key = ALIAS.GUIDEBOOK;

                if(serviceAlias === SERVICE_ALIAS.LINEAGE2) {
                    const isAwaken = alias === BE_ALIAS.AWAKENING_GUIDEBOOK;
                    title = `${isAwaken ? `${l10n?.filter?.l2AwknServer} ` : `${l10n?.filter?.l2Server} `}${l10n?.alias?.guidebook}`;
                    moreName = `${title} ${l10n.template.more}`;
                    moreUrl = `${getInitialUrl({ serviceAlias, alias: key, url: `/${key}`, searchParams: {
                        [SEARCH_PARAMS.KEYWORD]: searchParams?.get(SEARCH_PARAMS.KEYWORD) || '',
                        [SEARCH_PARAMS.GAME_NAME]: isAwaken ? GAME_NAME[SERVICE_ALIAS.LINEAGE2].L2AWKN : GAME_NAME[SERVICE_ALIAS.LINEAGE2].L2
                    }})}`;
                } else if(serviceAlias === SERVICE_ALIAS.AION) {
                    const isClassic = alias === BE_ALIAS.CLASSIC_GUIDEBOOK;
                    title = `${alias === BE_ALIAS.CLASSIC_GUIDEBOOK ? `${l10n?.filter?.aionClassicServer} ` : `${l10n?.filter?.aionLiveServer} `}${l10n?.alias?.guidebook}`;
                    moreName = `${title} ${l10n.template.more}`;
                    moreUrl = `${getInitialUrl({ serviceAlias, alias: key, url: `/${key}`, searchParams: {
                        [SEARCH_PARAMS.KEYWORD]: searchParams?.get(SEARCH_PARAMS.KEYWORD) || '',
                        [SEARCH_PARAMS.GAME_NAME]: isClassic ? GAME_NAME[SERVICE_ALIAS.AION].CLASSIC : GAME_NAME[SERVICE_ALIAS.AION].LIVE
                    }})}`;
                } else {
                    title = `${l10n?.alias?.guidebook}`;
                    moreName = `${title} ${l10n.template.more}`;
                    moreUrl = `${getInitialUrl({ serviceAlias, alias: key, url: `/${key}`, searchParams })}`;
                }
            }
            break;
        case BE_ALIAS.DICTIONARY:
            component = DictionaryArticle;
            column = 2;
            break;
        case BE_ALIAS.CHARACTER:
            const isBns = serviceAlias === SERVICE_ALIAS.BNS;
            component = isBns ? CharacterBnsArticle : CharacterAionArticle;
            column = 2;
            break;
        case BE_ALIAS.GUILD: // 아이온 - 레기온은 guild api 로 호출함
            component = serviceAlias === SERVICE_ALIAS.AION ? LegionArticle : GuildArticle;
            column = 2;
            key = serviceAlias === SERVICE_ALIAS.AION ? ALIAS.LEGION : ALIAS.GUILD;
            title = serviceAlias === SERVICE_ALIAS.AION ? l10n?.alias?.legions : l10n?.alias?.guild;
            moreName = `${title} ${l10n.template.more}`;
            moreUrl = `${getInitialUrl({ serviceAlias, alias: key, url: `/${key}`, searchParams })}`;
            break;
        case BE_ALIAS.NPUSER:
            component = NpUserArticle;
            column = 2;
            break;
        case BE_ALIAS.MOVIE:
        case BE_ALIAS.VIDEO:
            component = [SERVICE_ALIAS.AION, SERVICE_ALIAS.BNS].includes(serviceAlias) ? NctvArticle : VideoArticle;
            column = 2;
            key = [SERVICE_ALIAS.AION, SERVICE_ALIAS.BNS].includes(serviceAlias) ? ALIAS.NCTV : ALIAS.VIDEO;
            title = l10n?.alias[key];
            moreName = `${title} ${l10n.template.more}`;
            moreUrl = `${getInitialUrl({ serviceAlias, alias: key, url: `/${key}`, searchParams })}`;
            break;
        case BE_ALIAS.PROMOTION:
            component = EventArticle;
            column = 2;
            key = ALIAS.EVENT;
            title = l10n?.alias[key];
            moreName = `${title} ${l10n.template.more}`;
            if(serviceAlias === SERVICE_ALIAS.LINEAGEM) {
                moreUrl = `${getInitialUrl({ serviceAlias, alias: key, url: `/${key}`, searchParams })}`;
                moreUrlType = 'ROUTE';
            } else {
                moreUrl = '/eventon';
                moreUrlType = 'LINK';
            }
            break;
        case BE_ALIAS.IMAGE:
            component = ImageArticle;
            column = 4;
            break;
        case BE_ALIAS.CONTENT_LIST:
            component = ContentListArticle;
            column = 2;
            moreUrl = serviceAlias === SERVICE_ALIAS.LINEAGE ? '/board/updatechronicle/list' : serviceAlias === SERVICE_ALIAS.LINEAGE2 ? '/history/index' : '';
            moreUrlType = 'LINK';
            break;
        case BE_ALIAS.TRADE:
            component = TradeArticle;
            column = 1;
            maxLength = 3;
            moreUrl = getTradeMoreUrl(searchParams?.get(SEARCH_PARAMS.KEYWORD) || '');
            moreUrlType = 'LINK';
            break;
        default:
            component = Fragment;
            column = 0;
    }

    return { key, component, column, title, moreName, moreUrl, moreUrlType, maxLength };
};

// 서비스 별 콘텐츠 정렬 순서에 맞게 데이터 정렬
const getTotalDataSort = (data: ITotal<IOption['serviceAlias']>, serviceAlias: IOption['serviceAlias']): [string, any][] => {
    if(!data || typeof data !== 'object') {
        return [];
    }

    const sortKey = TOTAL_SORT[serviceAlias];

    return sortKey?.map(beAlias => [beAlias, data[beAlias]]);
};

// 2차 오픈 컴포넌트
const hideAliasArray = !window.userData?.isLogin && CUR_NETWORK !== NETWORK.OPDEV ? [BE_ALIAS.DIRECT, BE_ALIAS.GOODS, ALIAS.TRADE] : [BE_ALIAS.DIRECT, BE_ALIAS.GOODS];
export const Total = () => {
    const { data, isLoading, isError } = useLoaderData() as ILoaderData<IContentData<ITotal<typeof serviceAlias>>> || {};
    const option = useOption();
    const l10n = useAppSelector(selectL10N);
    const { serviceAlias } = option;
    //const totalDataArr = useMemo(() => data && typeof data === 'object' ? Object.entries(data) : [], [data]);
    const totalDataArr = useMemo(() => data && typeof data === 'object' ? getTotalDataSort(data, serviceAlias) : [], [data]);
    const isTotalDataEmpty = useMemo(() => totalDataArr?.length === 0 || totalDataArr?.every(data => !data[1]?.list || data[1]?.list?.length === 0), [totalDataArr]);
    const [searchParams, setSearchParams] = useSearchParams();
    const [isImageOpen, setIsImageOpen] = useState<boolean>(false);
    const [isVideoOpen, setIsVideoOpen] = useState<boolean>(false);
    const [modalImageData, setModalImageData] = useState<IModal['data']>({ url: null, title: null });
    const [modalVideoData, setModalVideoData] = useState<IModal['data']>({ url: null });

    const imageClickCallback = useCallback((modalData: IModal['data']) => {
        setIsImageOpen(true);
        setModalImageData(modalData);
    }, []);

    const btnClickCallback = useCallback((url: string | null) => {
        setIsVideoOpen(true);
        setModalVideoData({ url });
    }, []);
    
    if(isError || !Array.isArray(totalDataArr) || isTotalDataEmpty) {
        return <NoData />;
    }

    return <div className="search-result-wrap total">
        {
            Array.isArray(data[BE_ALIAS.GOODS]?.list) && data[BE_ALIAS.GOODS]?.list?.length > 0 ?
            <div className={`search-result nshop ${serviceAlias}`}>
                <h2>{l10n?.alias?.nshop}</h2>
                <ul className="column column-4">
                    {
                        data[BE_ALIAS.GOODS]?.list?.slice(0, MAX_LENGTH)?.map((v: INshop, i: number) => 
                            <li key={`${BE_ALIAS.NSHOP}_${i}`} className="search-item">
                                <NshopArticle item={v} l10n={l10n!} option={option} isGoodsLive={true} />
                            </li>)
                    }
                </ul>
                <AllMore 
                    name={`${l10n?.alias?.nshop} ${l10n?.template?.go}`} 
                    l10n={l10n!}
                    url={serviceAlias ? `${NSHOP_DOMAIN[CUR_NETWORK]}/${NSHOP_ALIAS[serviceAlias]}` : ''} />
            </div>
            : <Fragment />
        }
        {
            Array.isArray(totalDataArr) && totalDataArr?.length > 0 ?
            totalDataArr?.map((data, index) => {
                const value = data[1];
                const { 
                    key, 
                    component, 
                    column, 
                    title, 
                    moreName, 
                    moreUrl, 
                    moreUrlType, 
                    maxLength 
                } = getArticle({ option, l10n: l10n!, alias: data[0], searchParams });
                let list = value?.list || value?.contentList || [];
                let total = value?.pagination?.total || value?.searchCount;
                const isShow = !hideAliasArray?.includes(key);

                return isShow && list?.length > 0 ? 
                    <div key={`${key}_${index}`} className={`search-result ${key} ${serviceAlias}`}>
                        <div className="search-content">
                            <h2>{ title }</h2>
                            <ul className={`column column-${column}`}>
                                {
                                    list?.slice(0, maxLength).map((item, index) => {
                                        let props = {item, l10n, searchParams, option };

                                        if(key === ALIAS.IMAGE) {
                                            props = {...props, ...{imageClickCallback}};
                                        }

                                        if(key === ALIAS.VIDEO) {
                                            props = {...props, ...{btnClickCallback}};
                                        }

                                        return <li key={`${key}_${index}`} className="search-item">
                                                { component === Fragment ? createElement(component) : createElement(component, props) }
                                            </li>;
                                    })
                                }
                            </ul>
                            <AllMore 
                                name={ moreName } 
                                count={ total || 0 }
                                l10n={ l10n! }
                                type={ moreUrlType }
                                url={ moreUrl } />
                        </div>
                    </div> : <Fragment key={key} />
            }) : <Fragment />
        }
        <Modal type="image" isOpen={isImageOpen} setIsOpen={setIsImageOpen} data={modalImageData} />
        <Modal type="video" isOpen={isVideoOpen} setIsOpen={setIsVideoOpen} data={modalVideoData} />
    </div>;
};